/**
 * Typed client for the customer API (ADR-0026).
 *
 * Types come from `src/schema.d.ts`, generated from `openapi.json`, which the
 * backend exports with `python -m app.openapi_export`. Regenerate both when the
 * API changes (`pnpm api:generate`); a backend test fails if they drift.
 *
 * Authentication is the HttpOnly session cookie (never visible to JavaScript).
 * The CSRF token is kept in memory only and sent on state-changing requests.
 */
import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";

export type { components, paths } from "./schema";
export type Schemas = components["schemas"];

export const CSRF_HEADER = "X-CSRF-Token";
export const ORGANIZATION_HEADER = "X-Organization-ID";
export const REQUEST_ID_HEADER = "X-Request-ID";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export interface FieldError {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

/** An RFC 7807 problem returned by the API, or a network failure (status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string | undefined;
  readonly errors: FieldError[];
  readonly requestId: string | undefined;
  readonly retryAfter: number | undefined;

  constructor(init: {
    status: number;
    code: string;
    title: string;
    detail?: string | undefined;
    errors?: FieldError[] | undefined;
    requestId?: string | undefined;
    retryAfter?: number | undefined;
  }) {
    super(init.detail ?? init.title);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.detail = init.detail;
    this.errors = init.errors ?? [];
    this.requestId = init.requestId;
    this.retryAfter = init.retryAfter;
  }

  /** Message for one form field (`loc` ends with the field name), if any. */
  fieldError(field: string): string | undefined {
    return this.errors.find((e) => e.loc?.[e.loc.length - 1] === field)?.msg;
  }
}

export async function toApiError(response: Response, body: unknown): Promise<ApiError> {
  const problem = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  const type = typeof problem.type === "string" ? problem.type : "";
  const retry = Number(response.headers.get("Retry-After"));
  return new ApiError({
    status: response.status,
    code: type.startsWith("/problems/") ? type.slice("/problems/".length) : "http_error",
    title: typeof problem.title === "string" ? problem.title : response.statusText || "Error",
    detail: typeof problem.detail === "string" ? problem.detail : undefined,
    errors: Array.isArray(problem.errors) ? (problem.errors as FieldError[]) : undefined,
    requestId:
      (typeof problem.request_id === "string" ? problem.request_id : undefined) ??
      response.headers.get(REQUEST_ID_HEADER) ??
      undefined,
    retryAfter: Number.isFinite(retry) && retry > 0 ? retry : undefined,
  });
}

export interface ClientContext {
  /** Current CSRF token (memory only). */
  csrfToken(): string | null;
  /** Organization the current page works in, if any. */
  organizationId(): string | null;
  /** Called on any 401 so the app can return to the sign-in page. */
  onUnauthorized?(error: ApiError): void;
}

/**
 * A typed client for one API surface (customer or admin), with the shared
 * CSRF / organization headers. Same-origin by default: an absolute origin also
 * works where `Request` cannot resolve relative URLs (tests, workers).
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function createTypedClient<P extends {}>(context: ClientContext, baseUrl = globalThis.location?.origin ?? "") {
  const client = createClient<P>({
    baseUrl,
    credentials: "same-origin",
    // Resolve fetch per call, not once at creation, so instrumentation and test doubles apply.
    fetch: (request) => globalThis.fetch(request),
  });
  const headers: Middleware = {
    onRequest({ request }) {
      const csrf = context.csrfToken();
      if (csrf && !SAFE_METHODS.has(request.method)) request.headers.set(CSRF_HEADER, csrf);
      const organization = context.organizationId();
      if (organization && !request.headers.has(ORGANIZATION_HEADER)) {
        request.headers.set(ORGANIZATION_HEADER, organization);
      }
      return request;
    },
  };
  client.use(headers);
  return client;
}

/** The customer API client (dashboard). */
export function createApiClient(context: ClientContext, baseUrl?: string) {
  return createTypedClient<paths>(context, baseUrl);
}

export type ApiClient = ReturnType<typeof createApiClient>;

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Await an openapi-fetch call and return its data, or throw `ApiError`.
 * Network failures become `ApiError` with status 0.
 */
export async function unwrap<T>(
  call: Promise<FetchResult<T>>,
  context?: Pick<ClientContext, "onUnauthorized">,
): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await call;
  } catch {
    throw new ApiError({
      status: 0,
      code: "network_error",
      title: "Network error",
      detail: "The server could not be reached. Check your connection and try again.",
    });
  }
  if (result.response.ok) return result.data as T;
  const error = await toApiError(result.response, result.error);
  if (error.status === 401) context?.onUnauthorized?.(error);
  throw error;
}

/** A sentence for the user; includes the request id so support can find the log entry. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const base = error.detail ?? error.title;
    if (error.status === 429 && error.retryAfter) return `${base} Try again in ${error.retryAfter}s.`;
    if (error.status >= 500 && error.requestId) return `${base} (reference ${error.requestId})`;
    return base;
  }
  return "Something went wrong. Please try again.";
}
